import { resolvePostLoginPath } from 'utils/postLoginNavigation';

const STORAGE_KEY = 'mt_return_to';

export function isSafeReturnPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//');
}

export function persistReturnTo(path: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  if (path && isSafeReturnPath(path)) {
    sessionStorage.setItem(STORAGE_KEY, path);
  }
}

export function peekReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  const value = sessionStorage.getItem(STORAGE_KEY)?.trim() || '';
  return value && isSafeReturnPath(value) ? value : null;
}

export function consumeReturnTo(): string | null {
  const value = peekReturnTo();
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return value;
}

export function captureReturnTo(
  searchParams: URLSearchParams,
  stateFrom?: string | null,
): string | null {
  const fromQuery = searchParams.get('returnTo')?.trim() || '';
  const fromState = stateFrom?.trim() || '';
  const value =
    (fromQuery && isSafeReturnPath(fromQuery) && fromQuery) ||
    (fromState && isSafeReturnPath(fromState) && fromState) ||
    peekReturnTo();
  persistReturnTo(value);
  return value;
}

export function withReturnToQuery(path: string, returnTo: string | null): string {
  if (!returnTo || !isSafeReturnPath(returnTo)) return path;
  const joiner = path.includes('?') ? '&' : '?';
  return `${path}${joiner}returnTo=${encodeURIComponent(returnTo)}`;
}

function pathWithSearch(pathname: string, params: URLSearchParams): string {
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
}

function parseAppPath(path: string): { pathname: string; params: URLSearchParams } | null {
  if (!isSafeReturnPath(path)) return null;
  const url = new URL(path, 'https://profile.myteacher.pro');
  return { pathname: url.pathname, params: url.searchParams };
}

const ROLE_TUTOR = 1;
const ROLE_STUDENT = 2;

function coerceRoleIds(roles: unknown): number[] {
  if (!Array.isArray(roles)) return [];
  const ids = roles
    .map((role) => Number(role))
    .filter((role) => role === ROLE_TUTOR || role === ROLE_STUDENT);
  return [...new Set(ids)];
}

/**
 * Cabinet after a finished registration.
 * The role chosen on the form is used when the API answer has no role list,
 * so the person is not sent to the login page.
 */
export function resolveRegistrationNavigation(input: {
  roles?: unknown;
  registrationRole?: string | null;
  studentType?: string | null;
  returnTo?: string | null;
}): { path: string; roles: number[]; preferredRoleId: number } | null {
  const chosen = (input.registrationRole || '').trim().toLowerCase();
  const chosenStudent = chosen === 'student';
  const chosenTutor = chosen === 'teacher' || chosen === 'tutor';
  let roles = coerceRoleIds(input.roles);
  if (!roles.length && chosenTutor) roles = [ROLE_TUTOR];
  if (!roles.length && chosenStudent) roles = [ROLE_STUDENT];
  if (!roles.length) return null;

  const asStudent =
    chosenStudent ||
    (!chosenTutor && roles.includes(ROLE_STUDENT) && !roles.includes(ROLE_TUTOR));

  if (asStudent && roles.includes(ROLE_STUDENT)) {
    return {
      path: resolveAfterStudentRegistrationPath(input.studentType, input.returnTo),
      roles,
      preferredRoleId: ROLE_STUDENT,
    };
  }

  const tutorPath = resolvePostLoginPath(roles, input.returnTo);
  return {
    path: tutorPath === '/login' ? '/teacher' : tutorPath,
    roles,
    preferredRoleId: ROLE_TUTOR,
  };
}

/** После регистрации ученика: "для себя" - мастер заявки; "для детей" - список заявок без create. */
export function resolveAfterStudentRegistrationPath(
  registrantType?: string | null,
  returnTo?: string | null,
): string {
  const parsed = returnTo ? parseAppPath(returnTo) : null;
  const isProfileReturn = Boolean(parsed?.pathname.startsWith('/profile'));
  const basePath = isProfileReturn && parsed ? parsed.pathname : '/profile/tasks';
  const params = isProfileReturn && parsed ? parsed.params : new URLSearchParams();

  if (registrantType === 'parent') {
    params.delete('create');
    return pathWithSearch(basePath.startsWith('/profile') ? basePath : '/profile/tasks', params);
  }

  const tasksPath = basePath.startsWith('/profile/tasks') ? basePath : '/profile/tasks';
  params.set('create', '1');
  return pathWithSearch(tasksPath, params);
}
